import { ImageProps, StaticImageData, getImageProps } from 'next/image';

type SourceProps = {
  src: StaticImageData;
  media?: string;
  sizes?: string;
} & Omit<ImageProps, 'src' | 'alt'>;

type PictureProps = {
  sources: SourceProps[];
  alt: string;
  fill?: boolean;
  className?: string;
  priority?: ImageProps['priority'];
};

/*
  This component uses next's 'getImageProps' to combine the functionality of next/image and the picture element (such as art direction).
  https://nextjs.org/docs/pages/api-reference/components/image#getimageprops

  Art Direction example: <Picture alt={altText} sources={[{ src: desktopImg, media: '(min-width: 640px)' }, { src: mobileImg }]} />
*/

export default function Picture({ sources, alt, fill = false, className, priority }: PictureProps) {
  let nextImgAttributes = {};
  return (
    <picture className={className}>
      {sources.map((source, index) => {
        const { src, media, ...imageProps } = source;
        const {
          props: { sizes, srcSet, ...imgAttributes }
        } = getImageProps({
          ...imageProps,
          alt,
          src,
          fill,
          priority
        });

        nextImgAttributes = imgAttributes;

        return <source key={index} media={media} sizes={sizes} srcSet={srcSet} />;
      })}
      {/* 
        getImageProps sets an aspect-ratio style based on the default image.
        This causes issues for images sources used at different widths that might have different aspect ratios.
        Let's use aspect-ratio: auto for now until a better solution is found or provided in the stable release.
      */}
      <img alt={alt} {...nextImgAttributes} style={{ aspectRatio: 'auto' }} />
    </picture>
  );
}
